import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react'
import { useMiniPlayer } from '../../../contexts/MiniPlayerContext'
import { cn } from '../../../lib/utils'

// Reddit gallery carousel. Swipe via touch OR the chevron buttons +
// dot row. Index lives in local state. Buttons stopPropagation so
// taps on chevrons don't bubble into the wrapping <Link>.
//
// The same component renders at two sizes:
//   - card variant  → max-h-[420px]
//   - hero variant  → max-h-[60vh] (article detail page)
const SWIPE_THRESHOLD_PX = 50

export function GalleryCarousel({ images, title, hero = false, onImageClick }) {
  const [i, setI] = useState(0)
  const [failed, setFailed] = useState(() => new Set())
  // Touch tracking. startX captures the initial finger position;
  // the move handler decides direction at the threshold and the end
  // handler fires the advance. We don't preventDefault on touchmove
  // because that would block vertical page scroll — instead we let
  // the gesture choose itself naturally based on dominant axis.
  const touchRef = useRef({ startX: 0, startY: 0, dx: 0, swiping: false })
  if (!images?.length) return null

  const stop = (e) => { e.preventDefault(); e.stopPropagation() }
  const advance = (delta) => {
    setI((cur) => (cur + delta + images.length) % images.length)
  }
  const prev = (e) => { stop(e); advance(-1) }
  const next = (e) => { stop(e); advance(1) }

  // Touch handlers. We commit to "this is a swipe" only when the
  // horizontal movement clearly outpaces vertical — that way the
  // user can still scroll the page through the carousel.
  const onTouchStart = (e) => {
    const t = e.touches[0]
    if (!t) return
    touchRef.current = { startX: t.clientX, startY: t.clientY, dx: 0, swiping: false }
  }
  const onTouchMove = (e) => {
    const t = e.touches[0]
    if (!t) return
    const dx = t.clientX - touchRef.current.startX
    const dy = t.clientY - touchRef.current.startY
    // Once we're confident this is horizontal, mark it as a swipe so
    // touchend fires the advance. Threshold is small (8px) to feel
    // responsive without firing on incidental finger drift.
    if (!touchRef.current.swiping && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) {
      touchRef.current.swiping = true
    }
    touchRef.current.dx = dx
  }
  const onTouchEnd = () => {
    const { dx, swiping } = touchRef.current
    if (!swiping) return
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX) return
    // RTL aware: in an RTL document, a left-swipe means "go to the
    // next item" in reading order (Arabic users read right-to-left,
    // so the "next" image is to their left). We detect direction
    // from the document direction at swipe time so the gesture
    // mirrors text reading direction.
    const isRTL = typeof document !== 'undefined' && document.documentElement.dir === 'rtl'
    const goNext = isRTL ? dx > 0 : dx < 0
    advance(goNext ? 1 : -1)
  }

  const cur = images[i]
  if (failed.has(i)) {
    const fallback = images.findIndex((_u, idx) => !failed.has(idx))
    if (fallback === -1) return null
    if (fallback !== i) setI(fallback)
  }

  return (
    <div
      className="relative w-full select-none"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <img
        src={(cur || '').replace(/&amp;/g, '&')}
        alt={title || ''}
        loading="lazy"
        decoding="async"
        referrerPolicy="no-referrer"
        onError={() => setFailed((prev) => new Set(prev).add(i))}
        onClick={(e) => {
          if (!onImageClick) return
          e.preventDefault()
          e.stopPropagation()
          onImageClick(i)
        }}
        className={cn(
          'w-full object-cover rounded-md bg-muted',
          hero ? 'max-h-[60vh]' : 'max-h-[420px]',
          onImageClick && 'cursor-zoom-in',
        )}
      />
      {images.length > 1 && (
        <>
          <button
            type="button"
            onClick={prev}
            className="absolute top-1/2 -translate-y-1/2 start-1 w-8 h-8 rounded-full bg-black/60 text-white inline-flex items-center justify-center backdrop-blur-sm hover:bg-black/80"
            aria-label="Previous"
          >
            <ChevronLeft className="w-4 h-4 rtl:-scale-x-100" />
          </button>
          <button
            type="button"
            onClick={next}
            className="absolute top-1/2 -translate-y-1/2 end-1 w-8 h-8 rounded-full bg-black/60 text-white inline-flex items-center justify-center backdrop-blur-sm hover:bg-black/80"
            aria-label="Next"
          >
            <ChevronRight className="w-4 h-4 rtl:-scale-x-100" />
          </button>
          <span className="absolute top-1.5 end-1.5 px-1.5 py-0.5 rounded text-[10px] font-mono font-semibold text-white bg-black/70" dir="ltr">
            {i + 1} / {images.length}
          </span>
          <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex gap-1">
            {images.map((_u, idx) => (
              <span
                key={idx}
                className={cn(
                  'w-1.5 h-1.5 rounded-full',
                  idx === i ? 'bg-white' : 'bg-white/40',
                )}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}

// Reddit serves video and audio as separate streams for v.redd.it.
// The naming convention varies because Reddit migrated over the
// years and never picked one canonical form:
//   - Legacy: /DASH_720.mp4, /DASH_480.mp4 → audio at /DASH_audio.mp4
//   - 2023-ish modern: /DASH_AUDIO_128.mp4, /DASH_AUDIO_64.mp4
//   - 2024+ "CMAF" rollout: /CMAF_720.mp4, /CMAF_360.mp4 →
//                          audio at /CMAF_AUDIO_128.mp4 etc.
//
// We can't reliably guess which a post uses without parsing the
// MPD manifest, so we build a candidate list and walk it via
// <audio onError>: each 404 advances to the next. If all fail
// the muted-only fallback fires with the "silent" badge.
//
// CRITICAL: the candidate list MUST match the prefix of the
// video URL (CMAF → CMAF_AUDIO_*) — Reddit doesn't cross-serve
// audio under the other prefix. We also include the opposite
// prefix as a low-priority fallback in case a post somehow has
// mixed conventions.
function deriveRedditAudioCandidates(videoUrl) {
  if (!videoUrl) return []
  try {
    const u = new URL(videoUrl)
    if (!/(^|\.)v\.redd\.it$/i.test(u.hostname)) return []
    // Detect the video URL's prefix (DASH or CMAF) so we generate
    // audio candidates under the same family first.
    const prefixMatch = u.pathname.match(/\/(DASH|CMAF)_[^/]+\.mp4$/i)
    if (!prefixMatch) return []
    const videoPrefix = prefixMatch[1].toUpperCase()
    const altPrefix = videoPrefix === 'DASH' ? 'CMAF' : 'DASH'
    const segmentTemplates = [
      'AUDIO_128.mp4',
      'AUDIO_64.mp4',
      'audio.mp4',
      'audio_64.mp4',
      'AUDIO_192.mp4',
    ]
    const base = u.pathname.replace(/\/(DASH|CMAF)_[^/]+\.mp4$/i, '/')
    // Build candidates: same-prefix first, then opposite-prefix as
    // a hail-Mary. Also try without the original query string in
    // case ?source=fallback breaks audio fetching for some posts.
    const out = []
    const seen = new Set()
    const push = (path, withQuery) => {
      const next = new URL(u.toString())
      next.pathname = path
      if (!withQuery) next.search = ''
      const url = next.toString()
      if (!seen.has(url)) { seen.add(url); out.push(url) }
    }
    for (const prefix of [videoPrefix, altPrefix]) {
      for (const seg of segmentTemplates) {
        push(`${base}${prefix}_${seg}`, true)
      }
    }
    // Repeat the highest-confidence candidate without the query
    // string in case ?source=fallback is what's failing.
    for (const prefix of [videoPrefix, altPrefix]) {
      push(`${base}${prefix}_AUDIO_128.mp4`, false)
      push(`${base}${prefix}_audio.mp4`, false)
    }
    return out
  } catch {
    return []
  }
}

// Inline v.redd.it video player. Plays audio via a paired hidden
// <audio> element; falls back to muted-only when ALL audio candidate
// URLs 404 (older posts or silent uploads).
export function VideoBlock({ videoUrl, thumbnail, title, t, hero = false }) {
  const { t: tInline } = useTranslation()
  const [playing, setPlaying] = useState(false)
  const audioCandidates = useMemo(() => deriveRedditAudioCandidates(videoUrl), [videoUrl])
  // candidateIdx walks through the candidate list on each audio
  // <audio> onError. When it overruns the list we set hasAudio=false
  // and surface the muted badge.
  const [candidateIdx, setCandidateIdx] = useState(0)
  const videoRef = useRef(null)
  const audioRef = useRef(null)
  const audioUrl = audioCandidates[candidateIdx] || null
  const hasAudio = !!audioUrl
  const { openMini } = useMiniPlayer()
  const className = cn(
    'w-full object-contain bg-black',
    hero ? 'max-h-[60vh]' : 'max-h-[420px]',
  )

  // Bind the audio track to the video's playback. We attach handlers
  // directly on the video element so events fire even when the user
  // uses native controls (which we expose). The audio stays mute=
  // false; muting the video element so the browser uses the audio
  // track as the only sound source would also work, but native
  // controls show the speaker icon on the video, which is correct
  // since the visible element is the one with audio from the user's
  // POV.
  useEffect(() => {
    if (!playing || !hasAudio) return undefined
    const v = videoRef.current
    const a = audioRef.current
    if (!v || !a) return undefined
    const onPlay = () => { a.currentTime = v.currentTime; a.play().catch(() => {}) }
    const onPause = () => { a.pause() }
    const onSeeked = () => { a.currentTime = v.currentTime }
    const onRate = () => { a.playbackRate = v.playbackRate }
    const onVolume = () => { a.volume = v.volume; a.muted = v.muted }
    v.addEventListener('play', onPlay)
    v.addEventListener('pause', onPause)
    v.addEventListener('seeked', onSeeked)
    v.addEventListener('ratechange', onRate)
    v.addEventListener('volumechange', onVolume)
    return () => {
      v.removeEventListener('play', onPlay)
      v.removeEventListener('pause', onPause)
      v.removeEventListener('seeked', onSeeked)
      v.removeEventListener('ratechange', onRate)
      v.removeEventListener('volumechange', onVolume)
    }
  }, [playing, hasAudio])

  if (playing) {
    return (
      <div className="relative w-full rounded-md overflow-hidden bg-black">
        <video
          ref={videoRef}
          src={videoUrl}
          poster={thumbnail || undefined}
          controls
          playsInline
          // muted only when there's no paired audio. With audio, we
          // let the video element handle the volume UI (native
          // controls) and proxy that to the hidden audio element.
          muted={!hasAudio}
          className={className}
        />
        {hasAudio && (
          <audio
            // key on the URL so changing the candidate forces a
            // fresh <audio> rather than the browser caching a
            // failed-load state from the previous src.
            key={audioUrl}
            ref={audioRef}
            src={audioUrl}
            preload="auto"
            onError={() => {
              // Walk to the next candidate; if we've exhausted the
              // list, candidateIdx goes past the end and audioUrl
              // becomes null on next render — hasAudio flips false
              // and the muted badge surfaces.
              setCandidateIdx((cur) => cur + 1)
            }}
            style={{ display: 'none' }}
          />
        )}
        {/* Pop-out into the global floating mini player — keeps the
            video playing while the user navigates elsewhere. */}
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            openMini({
              kind: 'reddit-mp4',
              id: videoUrl,
              src: videoUrl,
              title,
              audioSrc: audioUrl || undefined,
            })
            setPlaying(false)
          }}
          aria-label={tInline('miniPlayer.popOut')}
          title={tInline('miniPlayer.popOut')}
          className="absolute top-1.5 start-1.5 w-7 h-7 rounded-full bg-black/70 text-white inline-flex items-center justify-center hover:bg-black/85 z-10"
        >
          <Maximize2 className="w-3.5 h-3.5" />
        </button>
        {!hasAudio && (
          <span className="absolute top-1.5 end-1.5 px-1.5 py-0.5 rounded text-[10px] font-semibold text-white bg-black/70">
            {t('news.reddit.silent')}
          </span>
        )}
      </div>
    )
  }
  return (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPlaying(true) }}
      className="relative w-full block rounded-md overflow-hidden bg-muted"
      aria-label={title}
    >
      {thumbnail && (
        <img
          src={thumbnail.replace(/&amp;/g, '&')}
          alt=""
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          className={className.replace('object-contain', 'object-cover')}
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
      <div className="absolute inset-0 flex items-center justify-center">
        <span className={cn(
          'rounded-full bg-orange-600/90 flex items-center justify-center shadow-lg',
          hero ? 'w-20 h-20' : 'w-14 h-14',
        )}>
          <svg
            width={hero ? '28' : '20'}
            height={hero ? '32' : '22'}
            viewBox="0 0 20 22"
            fill="white"
          >
            <polygon points="2,1 20,11 2,21" />
          </svg>
        </span>
      </div>
    </button>
  )
}
